import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Radio, RadioGroup, Checkbox, Tooltip, useMediaQuery } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import { getFullDate } from '../../../util';
import './GenerateDietModal.css';
import { useAppContext } from '../../../context/AppContext';
import axios from 'axios';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import { useNotification } from '../../notifications/useNotification';
import { capitalizeFirst } from '../../../utils/functions';
import TourButton from '../../TourButton/TourButton';
import { getGenerateDietModalSteps } from '../../../tours/generateDietModal';

/** Shared sx for all three Autocomplete pickers — transparent inner border, outer wrapper provides border */
const autocompleteTransparentSx = {
    '.MuiInputBase-root': { minHeight: 90, maxHeight: 140, overflowY: 'auto' },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: 'transparent' },
        '&:hover fieldset': { borderColor: 'transparent' },
        '&.Mui-focused fieldset': { borderColor: 'transparent' },
    },
};

const GenerateDietModal = ({ setShowModal, client, onSubmit }) => {
    const { t } = useTranslation();
    const tourSteps = useMemo(() => getGenerateDietModalSteps(t), [t]);
    const { showSuccess, showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [startDate, setStartDate] = useState(dayjs(getFullDate(new Date())));
    const [endDate, setEndDate] = useState(dayjs(''));
    const [tabValue, setTabValue] = useState("calories");
    const [dietLanguage, setDietLanguage] = useState("it")
    const [formulaSelectValue, setFormulaSelectValue] = useState(0);
    const [activitySelectValue, setActivitySelectValue] = useState(1.55);
    const [clientGender, setClientGender] = useState('');
    const [likedFoodSearchList, setLikedFoodSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [likedFoodsList, setLikedFoodsList] = useState(client && client.likedFoods ? client.likedFoods : []); // the list of liked foods
    const [dislikedFoodSearchList, setDislikedFoodSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [dislikedFoodsList, setDislikedFoodsList] = useState(client && client.dislikedFoods ? client.dislikedFoods : []);  // the list of disliked foods
    const [foodIntolerancesSearchList, setFoodIntolerancesSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [foodIntolerancesList, setFoodIntolerancesList] = useState(client && client.intolerances ? client.intolerances : []);  // the list of intolerances
    const [illnessSearchList, setIllnessSearchList] = useState([]); // the list of illnesses we retrieve from DB when searching
    const [illnessesList, setIllnessesList] = useState(client && client.illnesses ? client.illnesses : []);  // the list of selected illnesses
    const [dietFormat, setDietFormat] = useState("weekly")
    const [numberOfAlternatives, setNumberOfAlternatives] = useState(3);
    const [dietType, setDietType] = useState('omnivore');
    const [mealSelection, setMealSelection] = useState({
        breakfast: t("breakfast"),
        snack1: null,
        lunch: t("lunch"),
        snack2: null,
        dinner: t("dinner"),
        snack3: null,
        preWorkout: null,
        postWorkout: null,
    });
    const [clientBMR, setClientBMR] = useState(0);
    const [clientTDE, setClientTDE] = useState(0);
    const [clientDailyIntake, setClientDailyIntake] = useState(0);
    const [caloriesFormData, setCaloriesFormData] = useState({
        clientWeight: 0,
        clientBodyFat: 0,
        clientHeight: 0,
        clientAge: 0,
    });
    const [macroFormData, setMacroFormData] = useState({
        fatsPercentage: 20,
        carbsPercentage: 60,
        proteinsPercentage: 20,
        fats: 0,
        carbs: 0,
        proteins: 0,
    });
    const { dietist, currentLanguage } = useAppContext();

    const adapterLocale = currentLanguage === 'it' ? 'it' : 'en-gb';
    const calendarTheme = createTheme({
        palette: { primary: { main: '#fd4701', light: '#fc9f5b', dark: '#830102' } },
    });

    useEffect(() => {
        let bmr;
        switch (formulaSelectValue) {
            case 1:
            case 2:
                if (caloriesFormData.clientWeight > 0 && caloriesFormData.clientHeight > 0 &&
                    caloriesFormData.clientAge > 0 && (clientGender === 0 || clientGender === 1)) {
                    // Mifflin-St Jeor Equation
                    if (formulaSelectValue === 1) {
                        if (clientGender === 0) bmr = (10*Number(caloriesFormData.clientWeight))+(6.25*Number(caloriesFormData.clientHeight))-(5*Number(caloriesFormData.clientAge))+5;
                        else bmr = (10*Number(caloriesFormData.clientWeight))+(6.25*Number(caloriesFormData.clientHeight))-(5*Number(caloriesFormData.clientAge))-161;
                    }
                    // Harris-Benedict Equation
                    else if (formulaSelectValue === 2) {
                        if (clientGender === 0) bmr = 88.362 + ((13.397*caloriesFormData.clientWeight) + (4.799*caloriesFormData.clientHeight) - (5.677*caloriesFormData.clientAge));
                        else bmr = 447.593 + ((9.247*caloriesFormData.clientWeight) + (3.098*caloriesFormData.clientHeight) - (4.330*caloriesFormData.clientAge));
                    }
                    bmr = Math.round(bmr);
                    setClientBMR(bmr);
                    setClientTDE(Math.round(bmr * activitySelectValue));
                    setClientDailyIntake(Math.round(bmr * activitySelectValue));
                } else {
                    setClientBMR(0);
                    setClientTDE(0);
                    setClientDailyIntake(0);
                }
                break;
            case 3:
                if (caloriesFormData.clientWeight > 0 && caloriesFormData.clientBodyFat > 0) {
                    // calculate the Lean Body Mass
                    let lbm = caloriesFormData.clientWeight * (1 - (caloriesFormData.clientBodyFat / 100));
                    // calculate the BMR
                    bmr = 370 + (21.6 * lbm);
                    bmr = Math.round(bmr);
                    setClientBMR(bmr);
                    setClientTDE(Math.round(bmr * activitySelectValue));
                    setClientDailyIntake(Math.round(bmr * activitySelectValue));
                } else {
                    setClientBMR(0);
                    setClientTDE(0);
                    setClientDailyIntake(0);
                }
                break;
            default:
                setClientBMR(0);
                setClientTDE(0);
        }
    }, [caloriesFormData, clientGender, formulaSelectValue, activitySelectValue]);

    useEffect(() => {
        // update the macros values based on the new daily intake
        setMacroFormData(prev => ({
            ...prev,
            fats: Math.round((clientDailyIntake * prev.fatsPercentage) / 100 / 9),
            carbs: Math.round((clientDailyIntake * prev.carbsPercentage) / 100 / 4),
            proteins: Math.round((clientDailyIntake * prev.proteinsPercentage) / 100 / 4),
        }));
    }, [clientDailyIntake]);

    const handleNumberFocus = (e) => e.target.select();

    const handleCaloriesFormDataInputChange = (e) => {
        const { name, value } = e.target;

        // remove the error counter on top the CALORIES tab if the edite value was in an error state
        if (errors.caloriesTabCounter && errors[name]) {
            errors.caloriesTabCounter--;
        }

        // reset the error when changing the value
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });

        if(Number.isNaN(value) || value === "") {
            setCaloriesFormData({
                ...caloriesFormData,
                [name]: 0
            });
        } else {
            setCaloriesFormData({
                ...caloriesFormData,
                [name]: parseFloat(value)
            });
        }
    };

    const handleMealSelectionChange = (event) => {
        const { name, checked } = event.target;
        if (checked) setMealSelection(prev => ({ ...prev, [name]: t(name) }));
        else setMealSelection(prev => ({ ...prev, [name]: null }));
    };

    const handleDietFormatChange = (event) => {
        setDietFormat(event.target.value);
        setNumberOfAlternatives(3);
        setErrors({
            ...errors,
            "number-of-alternatives": "",
            formatTabErrorsCounter: null,
        });
    }
    
    const handleMacroFormDataInputChange = (e) => {
        const { name, value } = e.target;
        const parsedValue = (Number.isNaN(value) || value === "") ? 0 : parseFloat(value);

        // re calculate the errors on the MACRO tab
        let macroTabErrorsCounter = errors.macroTabErrorCounter ? errors.macroTabErrorCounter : 0;
        if (errors.macroTabErrorCounter && errors[name]) {
            macroTabErrorsCounter--;
        }

        if (errors.macroTab) {
            macroTabErrorsCounter--;
        }

        // reset the error when changing the value
        setErrors(prev => {
            let updated = {...prev};
            delete updated[name];
            delete updated["macroTab"];
            updated["macroTabErrorCounter"] = macroTabErrorsCounter;
            return updated;
        });

        // Calculate new macros based on the percentage provided
        const updatedValues = {
            ...macroFormData,
            [name]: parsedValue,
            fats: name === "fatsPercentage" ? Math.round((clientDailyIntake * parsedValue) / 100 / 9) : macroFormData.fats,
            carbs: name === "carbsPercentage" ? Math.round((clientDailyIntake * parsedValue) / 100 / 4) : macroFormData.carbs,
            proteins: name === "proteinsPercentage" ? Math.round((clientDailyIntake * parsedValue) / 100 / 4) : macroFormData.proteins,
        };

        setMacroFormData(updatedValues);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleClientGenderSelectChange = (event) => {
        setClientGender(event.target.value);
    };
    
    const handleFormulaSelectChange = (event) => {
        const formula = event.target.value
        let caloriesTabErrorsCounter = 0;


        // reset the other formulas fields
        switch (formula){
            // custom value
            case 0:
                setActivitySelectValue(1.55);
                setClientGender('');
                setClientBMR(0);
                setClientTDE(0);
                setCaloriesFormData({
                    clientWeight: 0,
                    clientBodyFat: 0,
                    clientHeight: 0,
                    clientAge: 0,
                });
                
                setErrors(prev => {
                    const {clientWeight, clientHeight, clientAge, clientBodyFat, clientGender, ...rest} = prev;
                    return rest;
                });
                break
            // Mifflin-St Jeor / Harris-Benedict
            case 1:
            case 2:
                setClientGender('');
                setClientBMR(0);
                setClientTDE(0);
                setCaloriesFormData({
                    ...caloriesFormData,
                    clientBodyFat: 0,
                });

                if (errors.clientWeight) {
                    caloriesTabErrorsCounter += 1;
                }
                if (errors.clientHeight) {
                    caloriesTabErrorsCounter += 1;
                }
                if (errors.clientAge) {
                    caloriesTabErrorsCounter += 1;
                }

                setErrors(prev => {
                    const {clientBodyFat, ...rest} = prev;
                    return rest;
                });
                break
            // Katch-McArdle
            case 3:
                setActivitySelectValue(1.55);
                setClientGender('');
                setClientBMR(0);
                setClientTDE(0);
                setCaloriesFormData({
                    ...caloriesFormData,
                    clientHeight: 0,
                    clientAge: 0,
                });

                if (errors.clientWeight) {
                    caloriesTabErrorsCounter += 1;
                }
                if (errors.clientBodyFat) {
                    caloriesTabErrorsCounter += 1;
                }

                setErrors(prev => {
                    const {clientHeight, clientAge, ...rest} = prev;
                    return rest;
                });
                break
            default:
                break
        }

        if (errors.clientDailyIntake) {
            caloriesTabErrorsCounter += 1;
        }

        setErrors({
            ...errors,
            caloriesTabCounter: caloriesTabErrorsCounter,
        })
        
        setFormulaSelectValue(event.target.value);
    };

    const handleActivitySelectChange = (event) => {
        setActivitySelectValue(event.target.value);
    };

    const handleStartDateChange = (newDate) => {
        setStartDate(newDate);

        // Auto-set end date to the last day of the same month
        if (newDate && newDate.isValid() && (!endDate || !endDate.isValid())) {
            setEndDate(newDate.endOf('month'));
        }

        // Reset the startDate error if any
        setErrors(prev => {
            const { startDate, endDate, ...rest } = prev;
            return rest;
        });
    }

    const handleEndDateChange = (newDate) => {
        setEndDate(newDate)
        
        // Reset the endDate error if any
        setErrors(prev => {
            const { endDate, ...rest } = prev;   // removes endDate from the object
            return rest;
        });
    }

    const handleDietLanguageChange = (event) => {
        setDietLanguage(event.target.value);
    };

    const handleClientDailyIntakeChange = (event) => {
        const value = event.target.value
        // if we delete the content inside the field it becomes the empty string and that's not permitted
        if(Number.isNaN(value) || value === "") {
            setClientDailyIntake(0);
        } else {
            setClientDailyIntake(parseFloat(value));
        }

        // must update both CALORIES and MACRO tab error counter
        if (errors.clientDailyIntake) {
            errors["macroTabErrorCounter"]--;
            errors["caloriesTabCounter"]--;
        }

        // reset the error when changing the value
        setErrors(prev => {
            const { clientDailyIntake, ...rest } = prev; // remove 'clientDailyIntake' if present
            return rest;
        });

        // reset the formulas (Mifflin-St Jeor, Katch-McArdle, Harris-Benedict) values because the user is manually changing the daily intake value
        setClientBMR(0);
        setClientTDE(0);
        setFormulaSelectValue(0);
        setActivitySelectValue(1.55);
        setClientGender('');
        const updatedCaloriesFormData = {
            clientWeight: 0,
            clientBodyFat: 0,
            clientHeight: 0,
            clientAge: 0,
        };
        setCaloriesFormData(updatedCaloriesFormData);

        // update the macro form data
        const updatedValues = {
            ...macroFormData,
            fats: Math.round((value * macroFormData.fatsPercentage) / 100 / 9),
            carbs: Math.round((value * macroFormData.carbsPercentage) / 100 / 4),
            proteins: Math.round((value * macroFormData.proteinsPercentage) / 100 / 4),
        };
        setMacroFormData(updatedValues);
    };

    const handleFoodIntoleranceChange = async (e) => {
        const foodIntoleranceName = e.target.value;
        let result = await getFoodIntolerancesFilteredByName(foodIntoleranceName);
        setFoodIntolerancesSearchList(result);
    }

    const handleDietTypeChange = (event) => {
        setDietType(event.target.value);
    }

    const handleNumberOfAlternativesChange = async (event) => {
        const nOfAlternatives = event.target.value;
        setNumberOfAlternatives(nOfAlternatives);
        setErrors({
            ...errors,
            "number-of-alternatives": "",
            formatTabErrorsCounter: null,
        });
    }

    const getFoodIntolerancesFilteredByName = async (foodIntoleranceName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/food-intolerances/?nameFilter=${encodeURIComponent(foodIntoleranceName)}`;
        url += `&language=${encodeURIComponent(dietLanguage)}`;
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const getIllnessesFilteredByName = async (illnessName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/illnesses/?nameFilter=${encodeURIComponent(illnessName)}`;
        url += `&language=${encodeURIComponent(dietLanguage)}`;
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const handleIllnessChange = async (e) => {
        const illnessName = e.target.value;
        let result = await getIllnessesFilteredByName(illnessName);
        setIllnessSearchList(result);
    }

    const handleFoodNameChange = async (e) => {
        const foodName = e.target.value;
        const componentName = e.target.name; // the component name that called the function
        let result = await getFoodsFilteredByName(foodName);
        switch (componentName) {
            case "liked-foods":
                setLikedFoodSearchList(result);
                break;
            case "disliked-foods":
                setDislikedFoodSearchList(result);
                break;
            default:
                console.log("Error when selecting foods")
        }
    }

    const getFoodsFilteredByName = async (foodName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${encodeURIComponent(foodName)}`;
        url += `&language=${encodeURIComponent(dietLanguage)}`;
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const validateForm = () => {
        const newErrors = {};

        // security checks on dates
        if (!startDate || !startDate.isValid())
            newErrors.startDate = t("errorMessages.dateRequired");
        if (!endDate || !endDate.isValid())
            newErrors.endDate = t("errorMessages.dateRequired");
        if (startDate && endDate && startDate.isAfter(endDate)) {
            newErrors.startDate = t("errorMessages.startDateCantBeGreaterThanEndDate");
        }
        if (startDate && endDate && endDate.isBefore(startDate)) {
            newErrors.endDate = t("errorMessages.endDateCantBeLowerThanStartDate");
        }

        // security check on language
        if (dietLanguage === "" || dietLanguage === null)
            newErrors.dietLanguage = t("errorMessages.diet-language-required");

        // security check on CALORIES tab
        let caloriesTabErrorCounter = 0;
        if ((formulaSelectValue === 1 || formulaSelectValue === 2 || formulaSelectValue === 3) && caloriesFormData.clientWeight <= 0) {
            newErrors.clientWeight = t("errorMessages.must-be-greater-than-zero");
            caloriesTabErrorCounter += 1;
        }
        if ((formulaSelectValue === 1 || formulaSelectValue === 2) && caloriesFormData.clientHeight <= 0) {
            newErrors.clientHeight = t("errorMessages.must-be-greater-than-zero");
            caloriesTabErrorCounter += 1;
        }
        if ((formulaSelectValue === 1 || formulaSelectValue === 2) && caloriesFormData.clientAge <= 0) {
            newErrors.clientAge = t("errorMessages.must-be-greater-than-zero");
            caloriesTabErrorCounter += 1;
        }
        if ((formulaSelectValue === 1 || formulaSelectValue === 2) && clientGender === '') {
            newErrors.clientGender = t("errorMessages.select-gender");
            caloriesTabErrorCounter += 1;
        }
        if (formulaSelectValue === 3 && (caloriesFormData.clientBodyFat <= 0 || caloriesFormData.clientBodyFat > 100)) {
            newErrors.clientBodyFat = t("errorMessages.must-be-between-zero-and-hundred");
            caloriesTabErrorCounter += 1;
        }
        
        // security check on MACRO tab
        let macroTabErrorCounter = 0;
        if (macroFormData.fatsPercentage < 0 || macroFormData.fatsPercentage > 100) {
            newErrors.fatsPercentage = t("errorMessages.must-be-between-zero-and-hundred");
            macroTabErrorCounter += 1;
        }
        if (macroFormData.carbsPercentage < 0 || macroFormData.carbsPercentage > 100) {
            newErrors.carbsPercentage = t("errorMessages.must-be-between-zero-and-hundred");
            macroTabErrorCounter += 1;
        }
        if (macroFormData.proteinsPercentage < 0 || macroFormData.proteinsPercentage > 100) {
            newErrors.proteinsPercentage = t("errorMessages.must-be-between-zero-and-hundred");
            macroTabErrorCounter += 1;
        }
        if (macroFormData.fatsPercentage + macroFormData.carbsPercentage + macroFormData.proteinsPercentage !== 100){
            newErrors.macroTab = t("errorMessages.sum-must-be-equal-to-100");
            macroTabErrorCounter += 1;
        }

        // calories daily intake is common between CALORIES and MACRO tab
        if (clientDailyIntake <= 0) {
            newErrors.clientDailyIntake = t("errorMessages.must-be-greater-than-zero");
            caloriesTabErrorCounter += 1;
            macroTabErrorCounter += 1;
        }
        if (caloriesTabErrorCounter > 0) 
            newErrors.caloriesTabCounter = caloriesTabErrorCounter
        if (macroTabErrorCounter > 0)
            newErrors.macroTabErrorCounter = macroTabErrorCounter

        // check on FORMAT tab
        let formatTabErrorsCounter = 0;
        if (dietFormat === "alternatives" && (numberOfAlternatives < 1 || numberOfAlternatives > 3)) {
            newErrors["number-of-alternatives"] = t("errorMessages.number-of-alternatives");
            formatTabErrorsCounter++;
        }
        if (formatTabErrorsCounter > 0) 
            newErrors.formatTabErrorsCounter = formatTabErrorsCounter

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                setLoading(true);
                let payload = {
                    clientId: client._id,
                    dietist: dietist,
                    options: {
                        startDate: startDate,
                        endDate: endDate,
                        selectedMeals: mealSelection,
                        mealNumbers: Object.values(mealSelection).filter(v => v !== null).length,
                        dietLanguage: dietLanguage,
                        macronutrients: macroFormData,
                        dietFormat: dietFormat,
                        numberOfAlternatives: numberOfAlternatives,
                        dietType: dietType,
                        foodsInfo: {
                            likedFoods: likedFoodsList,
                            dislikedFoods: dislikedFoodsList,
                            intolerances: foodIntolerancesList,
                            illnesses: illnessesList,
                        },
                        dailyCalories: clientDailyIntake,
                    }
                }
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/diets/generate`, payload, { withCredentials: true });
                showSuccess(capitalizeFirst(t("diet-generation-process-started")))
                onSubmit();
            } catch (error) {
                console.error('Generate diet modal error:', error);
                showError(capitalizeFirst(t("error-starting-diet-generation-process")))
            } finally {
                setLoading(false);
                handleClose();
            }
        }
    }

    const handleClose = () => {
        setCaloriesFormData({
            clientWeight: 0,
            clientBodyFat: 0,
            clientHeight: 0,
            clientAge: 0,
        });
        setTabValue("calories");
        setActivitySelectValue(1.55);
        setFormulaSelectValue(0);
        setClientGender('');
        setClientBMR(0);
        setClientTDE(0);
        setClientDailyIntake(0);
        setDietFormat("weekly");
        setNumberOfAlternatives(3);
        setDietType('omnivore');
        setMacroFormData({
            fatsPercentage: 20,
            carbsPercentage: 60,
            proteinsPercentage: 20,
            fats: 0,
            carbs: 0,
            proteins: 0,
        });
        setIllnessSearchList([]);
        setIllnessesList([]);
        setErrors({});
        setShowModal(false);
    }

    return (
        <>
            <LoadingOverlay open={loading} />
            <ThemeProvider theme={calendarTheme}>
                <div className="gdm-overlay">
                    <div className="gdm-modal" data-tour="gdm-modal">

                        {/* ── Header ── */}
                        <div className="gdm-header">
                            <h2 className="gdm-title">{t('generate-diet')}</h2>
                            <div className='gdm-tutorial-btn'>
                                <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                            </div>
                            <button className="gdm-close-btn" onClick={handleClose} aria-label="Close">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* ── Body ── */}
                        <div className="gdm-body">

                            {/* Dates */}
                            <div className="gdm-row">
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                                    <DatePicker
                                        label={t('start-date')}
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                size: 'small', fullWidth: true,
                                                error: !!errors.startDate, helperText: errors.startDate,
                                                sx: { '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fc9f5b' } },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                                    <DatePicker
                                        label={t('end-date')}
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                size: 'small', fullWidth: true,
                                                error: !!errors.endDate, helperText: errors.endDate,
                                                sx: { '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fc9f5b' } },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </div>

                            {/* Language */}
                            <div className="gdm-field gdm-field-inline">
                                <label className="gdm-label">{t('Language')}</label>
                                <select className="gdm-select-lang" value={dietLanguage} onChange={handleDietLanguageChange}>
                                    <option value="it">IT</option>
                                    <option value="en">EN</option>
                                </select>
                                {errors.dietLanguage && <span className="gdm-error">{errors.dietLanguage}</span>}
                            </div>

                            {/* ── Tabs ── */}
                            <div data-tour="gdm-tabs">
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                textColor="primary"
                                indicatorColor="primary"
                                centered
                                sx={{ borderBottom: '1px solid #e2e8f0', minHeight: 40 }}
                            >
                                <Tab value="calories" sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} label={
                                    <Badge badgeContent={errors.caloriesTabCounter} color="error" sx={{ '& .MuiBadge-badge': { right: -6, top: -3 } }}>
                                        {t('Calories')}
                                    </Badge>
                                } />
                                <Tab value="macro" sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} label={
                                    <Badge badgeContent={errors.macroTabErrorCounter} color="error" sx={{ '& .MuiBadge-badge': { right: -6, top: -3 } }}>
                                        {t('Macro')}
                                    </Badge>
                                } />
                                <Tab value="meals" label={capitalizeFirst(t('meals'))} sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} />
                                <Tab value="food"  label={t('Food')}  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} />
                                <Tab value="health"  label={capitalizeFirst(t('health'))}  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} />
                                <Tab value="format" sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} label={
                                    <Badge badgeContent={errors.formatTabErrorsCounter} color="error" sx={{ '& .MuiBadge-badge': { right: -6, top: -3 } }}>
                                        {capitalizeFirst(t('format'))}
                                    </Badge>
                                } />
                            </Tabs>
                            </div>

                            {/* ── CALORIES TAB ── */}
                            {tabValue === 'calories' && (
                                <div className="gdm-tab-content">
                                    <p className="gdm-section-title">{t('bmr-and-tde')}</p>

                                    <div className="gdm-field gdm-field-inline">
                                        <label className="gdm-label">{t('Formula')}</label>
                                        <FormControl size="small">
                                            <Select value={formulaSelectValue} onChange={handleFormulaSelectChange} sx={{ minWidth: 190 }}>
                                                <MenuItem value={0}>{t('custom-calories')}</MenuItem>
                                                <MenuItem value={1}>{t('Mifflin-St Jeor')}</MenuItem>
                                                <MenuItem value={2}>{t('Harris-Benedict')}</MenuItem>
                                                <MenuItem value={3}>{t('Katch-McArdle')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    {(formulaSelectValue === 1 || formulaSelectValue === 2) && <>
                                        <div className="gdm-field gdm-field-inline">
                                            <label className="gdm-label">{t('Gender')}</label>
                                            <FormControl size="small" error={!!errors.clientGender}>
                                                <Select value={clientGender} onChange={handleClientGenderSelectChange} sx={{ minWidth: 120 }} displayEmpty>
                                                    <MenuItem value="" disabled><em>—</em></MenuItem>
                                                    <MenuItem value={0}>{t('Male')}</MenuItem>
                                                    <MenuItem value={1}>{t('Female')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {errors.clientGender && <span className="gdm-error">{errors.clientGender}</span>}
                                        </div>
                                        <div className="gdm-row">
                                            <div className="gdm-field">
                                                <label className="gdm-label">{t('Weight')}</label>
                                                <div className="gdm-input-unit-wrap">
                                                    <input className={`gdm-input${errors.clientWeight ? ' gdm-input-error' : ''}`} type="number" name="clientWeight" min="0" value={caloriesFormData.clientWeight} onChange={handleCaloriesFormDataInputChange} onFocus={handleNumberFocus} />
                                                    <span className="gdm-unit">kg</span>
                                                </div>
                                                {errors.clientWeight && <span className="gdm-error">{errors.clientWeight}</span>}
                                            </div>
                                            <div className="gdm-field">
                                                <label className="gdm-label">{t('Height')}</label>
                                                <div className="gdm-input-unit-wrap">
                                                    <input className={`gdm-input${errors.clientHeight ? ' gdm-input-error' : ''}`} type="number" name="clientHeight" min="0" value={caloriesFormData.clientHeight} onChange={handleCaloriesFormDataInputChange} onFocus={handleNumberFocus} />
                                                    <span className="gdm-unit">cm</span>
                                                </div>
                                                {errors.clientHeight && <span className="gdm-error">{errors.clientHeight}</span>}
                                            </div>
                                        </div>
                                        <div className="gdm-row">
                                            <div className="gdm-field">
                                                <label className="gdm-label">{t('Age')}</label>
                                                <div className="gdm-input-unit-wrap">
                                                    <input className={`gdm-input${errors.clientAge ? ' gdm-input-error' : ''}`} type="number" name="clientAge" min="0" value={caloriesFormData.clientAge} onChange={handleCaloriesFormDataInputChange} onFocus={handleNumberFocus} />
                                                    <span className="gdm-unit">{t('years')}</span>
                                                </div>
                                                {errors.clientAge && <span className="gdm-error">{errors.clientAge}</span>}
                                            </div>
                                            <div className="gdm-field" />
                                        </div>
                                    </>}

                                    {formulaSelectValue === 3 && (
                                        <div className="gdm-row">
                                            <div className="gdm-field">
                                                <label className="gdm-label">{t('Weight')}</label>
                                                <div className="gdm-input-unit-wrap">
                                                    <input className={`gdm-input${errors.clientWeight ? ' gdm-input-error' : ''}`} type="number" name="clientWeight" min="0" value={caloriesFormData.clientWeight} onChange={handleCaloriesFormDataInputChange} onFocus={handleNumberFocus} />
                                                    <span className="gdm-unit">kg</span>
                                                </div>
                                                {errors.clientWeight && <span className="gdm-error">{errors.clientWeight}</span>}
                                            </div>
                                            <div className="gdm-field">
                                                <label className="gdm-label">{t('fat')}</label>
                                                <div className="gdm-input-unit-wrap">
                                                    <input className={`gdm-input${errors.clientBodyFat ? ' gdm-input-error' : ''}`} type="number" name="clientBodyFat" min="0" value={caloriesFormData.clientBodyFat} onChange={handleCaloriesFormDataInputChange} onFocus={handleNumberFocus} />
                                                    <span className="gdm-unit">%</span>
                                                </div>
                                                {errors.clientBodyFat && <span className="gdm-error">{errors.clientBodyFat}</span>}
                                            </div>
                                        </div>
                                    )}

                                    {formulaSelectValue !== 0 && (
                                        <div className="gdm-stats-row">
                                            <div className="gdm-stat-card">
                                                <span className="gdm-stat-label">BMR</span>
                                                <span className="gdm-stat-value">{clientBMR > 0 ? clientBMR : '—'}</span>
                                            </div>
                                            <div className="gdm-field gdm-field-inline gdm-stat-activity">
                                                <label className="gdm-label">{t('Activity')}</label>
                                                <FormControl size="small">
                                                    <Select value={activitySelectValue} onChange={handleActivitySelectChange} sx={{ minWidth: 160 }}>
                                                        <MenuItem value={1.2}>{t('Sedentary')}</MenuItem>
                                                        <MenuItem value={1.375}>{t('lightly-active')}</MenuItem>
                                                        <MenuItem value={1.55}>{t('moderately-active')}</MenuItem>
                                                        <MenuItem value={1.75}>{t('very-active')}</MenuItem>
                                                        <MenuItem value={1.9}>{t('super-active')}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </div>
                                            <div className="gdm-stat-card">
                                                <span className="gdm-stat-label">TDE</span>
                                                <span className="gdm-stat-value">{clientTDE > 0 ? clientTDE : '—'}</span>
                                            </div>
                                        </div>
                                    )}

                                    <p className="gdm-section-title">{t('total-calories')}</p>
                                    <div className="gdm-field">
                                        <label className="gdm-label">{t('daily-intake')}</label>
                                        <div className="gdm-input-unit-wrap">
                                            <input className={`gdm-input${errors.clientDailyIntake ? ' gdm-input-error' : ''}`} type="number" name="clientDailyIntake" min="0" value={clientDailyIntake} onChange={handleClientDailyIntakeChange} onFocus={handleNumberFocus} />
                                            <span className="gdm-unit">kcal</span>
                                        </div>
                                        {errors.clientDailyIntake && <span className="gdm-error">{errors.clientDailyIntake}</span>}
                                    </div>
                                </div>
                            )}

                            {/* ── MACRO TAB ── */}
                            {tabValue === 'macro' && (
                                <div className="gdm-tab-content">
                                    <div className="gdm-field">
                                        <label className="gdm-label">{t('total-calories')}</label>
                                        <div className="gdm-input-unit-wrap">
                                            <input className={`gdm-input${errors.clientDailyIntake ? ' gdm-input-error' : ''}`} type="number" name="clientDailyIntake" min="0" value={clientDailyIntake} onChange={handleClientDailyIntakeChange} onFocus={handleNumberFocus} />
                                            <span className="gdm-unit">kcal</span>
                                        </div>
                                        {errors.clientDailyIntake && <span className="gdm-error">{errors.clientDailyIntake}</span>}
                                    </div>
                                    <div className="gdm-row">
                                        <div className="gdm-field">
                                            <label className="gdm-label">{t('Fats')}</label>
                                            <div className="gdm-input-unit-wrap">
                                                <input className={`gdm-input${errors.fatsPercentage ? ' gdm-input-error' : ''}`} type="number" name="fatsPercentage" min="0" max="100" value={macroFormData.fatsPercentage} onChange={handleMacroFormDataInputChange} onFocus={handleNumberFocus} />
                                                <span className="gdm-unit">%</span>
                                            </div>
                                            {errors.fatsPercentage && <span className="gdm-error">{errors.fatsPercentage}</span>}
                                        </div>
                                        <div className="gdm-field">
                                            <label className="gdm-label">{t('Carbohydrates')}</label>
                                            <div className="gdm-input-unit-wrap">
                                                <input className={`gdm-input${errors.carbsPercentage ? ' gdm-input-error' : ''}`} type="number" name="carbsPercentage" min="0" max="100" value={macroFormData.carbsPercentage} onChange={handleMacroFormDataInputChange} onFocus={handleNumberFocus} />
                                                <span className="gdm-unit">%</span>
                                            </div>
                                            {errors.carbsPercentage && <span className="gdm-error">{errors.carbsPercentage}</span>}
                                        </div>
                                    </div>
                                    <div className="gdm-row">
                                        <div className="gdm-field">
                                            <label className="gdm-label">{t('Proteins')}</label>
                                            <div className="gdm-input-unit-wrap">
                                                <input className={`gdm-input${errors.proteinsPercentage ? ' gdm-input-error' : ''}`} type="number" name="proteinsPercentage" min="0" max="100" value={macroFormData.proteinsPercentage} onChange={handleMacroFormDataInputChange} onFocus={handleNumberFocus} />
                                                <span className="gdm-unit">%</span>
                                            </div>
                                            {errors.proteinsPercentage && <span className="gdm-error">{errors.proteinsPercentage}</span>}
                                        </div>
                                        <div className="gdm-field" />
                                    </div>
                                    {errors.macroTab && <span className="gdm-error">{errors.macroTab}</span>}
                                    <div className="gdm-macro-summary">
                                        <div className="gdm-macro-chip">
                                            <span className="gdm-macro-chip-label">kcal</span>
                                            <span className="gdm-macro-chip-value">{clientDailyIntake}</span>
                                        </div>
                                        <div className="gdm-macro-chip">
                                            <span className="gdm-macro-chip-label">{t('Fats')}</span>
                                            <span className="gdm-macro-chip-value">{macroFormData.fats}g</span>
                                        </div>
                                        <div className="gdm-macro-chip">
                                            <span className="gdm-macro-chip-label">{t('Carbs')}</span>
                                            <span className="gdm-macro-chip-value">{macroFormData.carbs}g</span>
                                        </div>
                                        <div className="gdm-macro-chip">
                                            <span className="gdm-macro-chip-label">{t('Proteins')}</span>
                                            <span className="gdm-macro-chip-value">{macroFormData.proteins}g</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── MEALS TAB ── */}
                            {tabValue === 'meals' && (
                                <div className="gdm-tab-content">
                                    <div className="gdm-meals-grid">
                                        <FormControlLabel control={<Checkbox checked={mealSelection.breakfast !== null} onChange={handleMealSelectionChange} name="breakfast" />} label={t('breakfast')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.snack2 !== null}   onChange={handleMealSelectionChange} name="snack2" />}     label={t('snack2')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.snack1 !== null}   onChange={handleMealSelectionChange} name="snack1" />}     label={t('snack1')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.dinner !== null}   onChange={handleMealSelectionChange} name="dinner" />}     label={t('dinner')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.lunch !== null}    onChange={handleMealSelectionChange} name="lunch" />}      label={t('lunch')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.snack3 !== null}   onChange={handleMealSelectionChange} name="snack3" />}     label={t('snack3')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.preWorkout !== null}  onChange={handleMealSelectionChange} name="preWorkout" />}  label={t('preWorkout')} />
                                        <FormControlLabel control={<Checkbox checked={mealSelection.postWorkout !== null} onChange={handleMealSelectionChange} name="postWorkout" />} label={t('postWorkout')} />
                                    </div>
                                </div>
                            )}

                            {/* ── FOOD TAB ── */}
                            {tabValue === 'food' && (
                                <div className="gdm-tab-content">
                                    <div className="gdm-field">
                                        <label className="gdm-label">{t('liked-foods')}</label>
                                        <div className="gdm-autocomplete-wrap">
                                            <Autocomplete
                                                multiple freeSolo
                                                value={likedFoodsList}
                                                onChange={(_, newValue) => setLikedFoodsList(newValue)}
                                                options={likedFoodSearchList}
                                                getOptionLabel={(food) => {
                                                    if (typeof food === 'string') return food;
                                                    const name = food.name?.[dietLanguage] || '';
                                                    return food.brand ? `${name} - ${food.brand}` : name;
                                                }}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option._id}>
                                                        {option.brand ? `${option.name?.[dietLanguage]} - ${option.brand}` : option.name?.[dietLanguage]}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField {...params} onChange={handleFoodNameChange} placeholder={t('select-food')} name="liked-foods" />
                                                )}
                                                sx={autocompleteTransparentSx}
                                            />
                                        </div>
                                    </div>
                                    <div className="gdm-field">
                                        <label className="gdm-label">{t('disliked-foods')}</label>
                                        <div className="gdm-autocomplete-wrap">
                                            <Autocomplete
                                                multiple freeSolo
                                                value={dislikedFoodsList}
                                                onChange={(_, newValue) => setDislikedFoodsList(newValue)}
                                                options={dislikedFoodSearchList}
                                                getOptionLabel={(food) => {
                                                    if (typeof food === 'string') return food;
                                                    const name = food.name?.[dietLanguage] || '';
                                                    return food.brand ? `${name} - ${food.brand}` : name;
                                                }}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option._id}>
                                                        {option.brand ? `${option.name?.[dietLanguage]} - ${option.brand}` : option.name?.[dietLanguage]}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField {...params} onChange={handleFoodNameChange} placeholder={t('select-food')} name="disliked-foods" />
                                                )}
                                                sx={autocompleteTransparentSx}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── HEALTH TAB ── */}
                            {tabValue === 'health' && (
                                <div className="gdm-tab-content">
                                    <div className="gdm-field">
                                        <label className="gdm-label">{capitalizeFirst(t('intolerances'))}</label>
                                        <div className="gdm-autocomplete-wrap">
                                            <Autocomplete
                                                multiple freeSolo
                                                value={foodIntolerancesList}
                                                onChange={(_, newValue) => setFoodIntolerancesList(newValue)}
                                                options={foodIntolerancesSearchList}
                                                getOptionLabel={(intolerance) => intolerance.name[dietLanguage]}
                                                renderInput={(params) => (
                                                    <TextField {...params} onChange={handleFoodIntoleranceChange} placeholder={t('select-food-intolerance')} name="food-intolerance" />
                                                )}
                                                sx={autocompleteTransparentSx}
                                            />
                                        </div>
                                    </div>
                                    <div className="gdm-field">
                                        <label className="gdm-label">{capitalizeFirst(t('illnesses'))}</label>
                                        <div className="gdm-autocomplete-wrap">
                                            <Autocomplete
                                                multiple
                                                value={illnessesList}
                                                onChange={(_, newValue) => setIllnessesList(newValue)}
                                                options={illnessSearchList}
                                                getOptionLabel={(illness) => illness.name?.[dietLanguage] ?? ''}
                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                renderInput={(params) => (
                                                    <TextField {...params} onChange={handleIllnessChange} placeholder={t('select-illness')} name="illness" />
                                                )}
                                                sx={autocompleteTransparentSx}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── FORMAT TAB ── */}
                            {tabValue === 'format' && (
                                <div className="gdm-tab-content">
                                    <div className="gdm-field">
                                        <label className="gdm-label">{capitalizeFirst(t('diet-format'))}</label>
                                        <RadioGroup value={dietFormat} onChange={handleDietFormatChange}>
                                            <div className="gdm-format-option">
                                                <FormControlLabel value="weekly" control={<Radio />} label={t('weekly')} />
                                                <Tooltip title={t('weekly-explaination-info')}>
                                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8', cursor: 'pointer' }} />
                                                </Tooltip>
                                            </div>
                                            <div className="gdm-format-option">
                                                <FormControlLabel value="alternatives" control={<Radio />} label={t('alternatives')} />
                                                <Tooltip title={t('alternatives-explaination-info')}>
                                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8', cursor: 'pointer' }} />
                                                </Tooltip>
                                            </div>
                                        </RadioGroup>
                                        <div className="gdm-alternatives-field">
                                            <label className="gdm-label">{t('number-of-alternatives')}</label>
                                            <div className="gdm-input-unit-wrap">
                                                <input
                                                    className={`gdm-input${errors['number-of-alternatives'] ? ' gdm-input-error' : ''}`}
                                                    type="number" min="1" max="3"
                                                    value={numberOfAlternatives}
                                                    onChange={handleNumberOfAlternativesChange}
                                                    disabled={dietFormat !== 'alternatives'}
                                                />
                                            </div>
                                            {errors['number-of-alternatives'] && <span className="gdm-error">{errors['number-of-alternatives']}</span>}
                                        </div>
                                    </div>
                                    <div className="gdm-field gdm-field-inline">
                                        <label className="gdm-label">{capitalizeFirst(t('diet-type'))}</label>
                                        <FormControl size="small">
                                            <Select value={dietType} onChange={handleDietTypeChange} sx={{ minWidth: 190 }}>
                                                <MenuItem value="omnivore">{capitalizeFirst(t('omnivore'))}</MenuItem>
                                                <MenuItem value="vegan">{capitalizeFirst(t('vegan'))}</MenuItem>
                                                <MenuItem value="vegetarian">{capitalizeFirst(t('vegetarian'))}</MenuItem>
                                                <MenuItem value="carnivore">{capitalizeFirst(t('carnivore'))}</MenuItem>
                                                <MenuItem value="pescatarian">{capitalizeFirst(t('pescatarian'))}</MenuItem>
                                                <MenuItem value="mediterranean">{capitalizeFirst(t('mediterranean'))}</MenuItem>
                                                <MenuItem value="ketogenic">{capitalizeFirst(t('ketogenic'))}</MenuItem>
                                                <MenuItem value="paleo">{capitalizeFirst(t('paleo'))}</MenuItem>
                                                <MenuItem value="gluten_free">{capitalizeFirst(t('gluten_free'))}</MenuItem>
                                                <MenuItem value="lactose_free">{capitalizeFirst(t('lactose_free'))}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Footer ── */}
                        <div className="gdm-footer" data-tour="gdm-footer">
                            <button className="gdm-btn gdm-btn-cancel" onClick={handleClose}>{t('Cancel')}</button>
                            <button className="gdm-btn gdm-btn-save" onClick={handleSubmit}>{t('Generate')}</button>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        </>
    );
};

export default GenerateDietModal;
